import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { Award, BadgeDollarSign, Briefcase, Check, FileSearch, HeartHandshake, Home, MessageCircle, Scale, ScrollText, ShieldCheck } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import heroImage from "@/assets/home-hero-erin-buttons-closed.webp";
import qlsLogo from "@/assets/qls-member-logo.png";
import pexaLogo from "@/assets/pexa-member-logo.png";
import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  MICHAEL_PHONE_HREF,
  PENINSULA_OFFICE,
  PEOPLE,
  PRACTICE_AREAS,
  faqSchema,
  localBusinessSchema,
  organizationSchema,
} from "@/lib/firm";
import { GUIDES } from "@/lib/guides";

const AREA_ICONS: Record<string, LucideIcon> = {
  "/conveyancing": Home,
  "/wills-and-estates": ScrollText,
  "/elder-law": HeartHandshake,
  "/business-lawyers": Briefcase,
  "/independent-legal-advice": ShieldCheck,
  "/probate-queensland": Scale,
};

const AREA_BENEFITS: Record<string, string[]> = {
  "/conveyancing": [
    "Free contract review before you sign",
    "A solicitor, not just a clerk, on your file",
    "Electronic PEXA settlement, Queensland-wide",
  ],
  "/wills-and-estates": [
    "Fixed-price wills and enduring powers of attorney",
    "Home, hospital and aged-care visits",
    "Probate and estate administration handled start to finish",
  ],
  "/elder-law": [
    "Granny flat agreements that protect everyone",
    "Retirement village contracts reviewed",
    "Capacity and family-pressure concerns handled carefully",
  ],
  "/business-lawyers": [
    "Retail lease review and Form 12 certificates",
    "Business sales and purchases",
    "Commercial property conveyancing",
  ],
  "/independent-legal-advice": [
    "Independent advice before you sign",
    "Loan, guarantee and security documents reviewed",
    "Solicitor certificates for lender requirements",
  ],
  "/probate-queensland": [
    "Probate and letters of administration",
    "Guidance for executors and administrators",
    "Estate assets, accounts and distributions",
  ],
};

const HOME_PRACTICE_AREAS = [
  ...PRACTICE_AREAS,
  {
    to: "/independent-legal-advice",
    title: "Guarantees & Independent Solicitor Certificates",
    price: "",
    blurb: "Understand your obligations and what is at risk before signing a guarantee. Independent legal advice and solicitor certificates for loan and security documents.",
  },
  {
    to: "/probate-queensland",
    title: "Probate & Deceased Estate Administration",
    price: "",
    blurb: "Support after a death, from obtaining a grant of probate or letters of administration through to collecting assets and distributing the estate.",
  },
] as const;

const PERSON_ONE_LINER: Record<string, string> = {
  "michael-klein":
    "Admitted 2003, High Court and Supreme Court. Two decades of property, estate and litigation work. Lives in Moreton Bay.",
  "erin-wilkinson":
    "Years as a South East Queensland conveyancer before admission in 2025. Lives locally in Golden Beach.",
};

const FAQS = [
  {
    q: "Who does conveyancing in Golden Beach, QLD?",
    a: `Coastside Law does. We are a Queensland law practice based at ${FIRM_ADDRESS}, acting on residential sales and purchases in Golden Beach, Pelican Waters, Caloundra, Baringa and the surrounding suburbs, with a second office at Scarborough in Moreton Bay. In Queensland conveyancing must be done by a law practice, so a solicitor reviews your contract as part of the fixed fee.`,
  },
  {
    q: "How much does conveyancing cost with Coastside Law?",
    a: "We quote a fixed fee for residential sales and purchases before we start, so you know the legal cost up front. Government searches, transfer duty and registration fees are additional and are paid to third parties, not to us. Our current fixed fees are set out on our fees page.",
  },
  {
    q: "Can a buyer move in before settlement in Queensland?",
    a: "Only if the seller agrees and the arrangement is documented. Early possession leaves the seller on title, exposed on insurance and liable for outgoings, so it should be recorded in an occupation licence rather than a handshake. Our guide on possession before settlement in Queensland sets out the clause 8.5 position and the terms the licence should record.",
  },

  {
    q: "Where is Coastside Law located?",
    a: `We are at ${FIRM_ADDRESS} — in Golden Beach, a few minutes from central Caloundra. We act for clients across Caloundra, Pelican Waters, Kings Beach, Moffat Beach, Currimundi and the wider Sunshine Coast.`,
  },
  {
    q: "Do I need a lawyer or a conveyancer in Queensland?",
    a: "In Queensland, conveyancing must be carried out by a law practice — there is no separate licensed conveyancer profession as there is in New South Wales or Victoria. That means you get a lawyer's advice on the contract as part of the job.",
  },
  {
    q: "Can you review a contract before I sign it?",
    a: "Yes, free of charge, and it is the single most useful hour of our involvement. Send it through and we will tell you what it means, what is missing and what to negotiate before you are bound.",
  },
  {
    q: "Do you offer appointments outside business hours?",
    a: "Yes. Our usual hours are Monday to Friday, 8:30am to 5:00pm, and we arrange after-hours and home appointments where that is easier — particularly for elderly clients and wills signings.",
  },
];

const HOME_TITLE = `Conveyancing Golden Beach QLD — ${FIRM.name} | Caloundra & Scarborough Lawyers`;
/** Local-pack style description: name, street, suburb, phone and hours in the first 160 chars. */
const HOME_DESCRIPTION = `${FIRM.name}, ${FIRM.street}, ${FIRM.suburb} QLD ${FIRM.postcode}. Fixed-fee conveyancing, wills, probate and business law for Caloundra and Scarborough. Call ${FIRM.phone}, Mon–Fri 8:30am–5pm.`;

/** Permanent Google Business Profile link (Google's own CID URL for the listing). */
const GOOGLE_PROFILE_URL = "https://maps.google.com/?cid=17645464718929886346";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: fitTitle(HOME_TITLE) },
      { name: "description", content: fitDescription(HOME_DESCRIPTION) },
      { property: "og:title", content: fitTitle(HOME_TITLE) },
      { property: "og:description", content: fitDescription(HOME_DESCRIPTION) },
      { property: "og:url", content: "https://coastsidelaw.com.au" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_AU" },
      { name: "geo.region", content: "AU-QLD" },
      { name: "geo.placename", content: "Golden Beach, Caloundra, Queensland" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(organizationSchema) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          ...localBusinessSchema,
          "@id": "https://coastsidelaw.com.au/#office",
          url: "https://coastsidelaw.com.au",
          sameAs: [GOOGLE_PROFILE_URL, "https://www.linkedin.com/company/143455959/", "https://www.instagram.com/coastsidelaw/", "https://www.facebook.com/profile.php?id=61586552011485"],
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),

  component: HomePage,
});

function HomePage() {
  return (
    <>
      <section className="relative isolate overflow-hidden bg-primary pt-20 lg:pt-0">
        <img
          src={heroImage}
          alt="Erin Wilkinson, lawyer at Coastside Law, beside the waterfront"
          className="h-80 w-full object-cover object-[65%_30%] sm:h-96 lg:absolute lg:inset-0 lg:h-full lg:object-center"
          fetchPriority="high"
        />
        {/* Keep the portrait clear, with contrast confined to desktop text and navigation. */}
        <div className="absolute inset-0 hidden bg-gradient-to-r from-foreground/95 via-foreground/85 via-40% to-transparent to-60% lg:block" />
        <div className="absolute inset-x-0 top-0 hidden h-28 bg-gradient-to-b from-foreground/85 to-transparent lg:block" />

        <div className="relative mx-auto max-w-6xl px-5 pb-14 pt-8 sm:pb-20 lg:pt-36">
          <div className="max-w-2xl lg:max-w-[48%]">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-primary-foreground/70">
              Sunshine Coast · Moreton Bay · Wide Bay
            </p>
            <h1 className="mt-4 font-serif text-4xl leading-[1.1] text-primary-foreground sm:text-5xl">
              Your local lawyers for property, wills and business
            </h1>
            <p className="measure mt-6 text-lg leading-relaxed text-primary-foreground/90">
              Solicitor-led conveyancing, wills and estates, elder law and business law for the
              Sunshine Coast, Moreton Bay and Wide Bay — offices at Golden Beach and Scarborough,
              acting Queensland-wide.
            </p>

            <p className="measure mt-3 text-base font-semibold text-primary-foreground">
              Over 40 years&apos; combined experience. Thousands of Queensland property settlements
              completed.
            </p>
            <a
              href={GOOGLE_PROFILE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-3 rounded-full bg-card px-4 py-2 text-sm font-semibold text-foreground shadow-md transition-transform hover:-translate-y-0.5"
              aria-label="Rated 5.0 out of 5 on Google — read our reviews on our Google Business Profile"
            >
              <span className="font-serif text-base">5.0</span>
              <span className="tracking-widest text-brand-gold" aria-hidden="true">★★★★★</span>
              <span className="text-muted-foreground">Google reviews · verify on Google →</span>
            </a>
            <ul className="mt-6 grid grid-cols-2 gap-x-4 gap-y-2 text-sm font-semibold text-primary-foreground">
              {["Practising since 2003", "Fixed fees, quoted upfront", "Golden Beach & Scarborough offices", "Solicitor-led, start to finish"].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8 grid gap-3 sm:flex sm:flex-wrap">
              <a
                href={FIRM.phoneHref}
                className="inline-flex items-center justify-center rounded-md border border-primary-foreground/60 px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-foreground/10"
              >
                Call {FIRM.phone}
              </a>
            </div>

            <p className="mt-6 text-xs text-primary-foreground/70">
              {FIRM_ADDRESS}
            </p>
          </div>

        </div>
      </section>

      {/* Trust band — memberships directly under the hero */}
      <section aria-label="Memberships" className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-3 px-5 py-5 sm:flex-row sm:justify-center sm:gap-12">
          <div className="flex items-center gap-8 sm:gap-12">
            <img src={qlsLogo} alt="Member of the Queensland Law Society" width={281} height={100} className="h-11 w-auto sm:h-14" />
            <img src={pexaLogo} alt="PEXA member — electronic conveyancing" width={195} height={100} className="h-11 w-auto sm:h-14" />
          </div>
          <p className="max-w-xs text-center text-sm text-muted-foreground sm:text-left">
            Queensland Law Society members. Electronic settlement through PEXA.
          </p>
        </div>
      </section>

      {/* Practice areas */}
      <section className="mx-auto max-w-6xl px-5 py-20">
        <h2 className="font-serif text-3xl text-foreground sm:text-4xl">How we help</h2>
        <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {HOME_PRACTICE_AREAS.map((area) => {
            const Icon = AREA_ICONS[area.to] ?? Home;
            return (
              <Link
                key={area.to}
                to={area.to}
                className="group flex flex-col rounded-sm border border-border bg-card p-7 card-elevate"
              >
                <span className="inline-flex size-24 items-center justify-center rounded-full bg-accent/20 text-primary">
                  <Icon className="size-12" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-serif text-2xl text-foreground">{area.title}</h3>
                {area.price && <p className="mt-1 text-sm font-semibold text-primary">{area.price}</p>}
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {area.blurb}
                </p>
                <ul className="mt-4 flex-1 space-y-2 text-sm text-foreground">
                  {(AREA_BENEFITS[area.to] ?? []).map((b) => (
                    <li key={b} className="flex gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <span className="mt-5 text-sm font-semibold text-primary group-hover:underline">
                  Read more →
                </span>
              </Link>
            );
          })}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          All fixed fees, agreed before we start.{" "}
          <Link to="/fees" className="font-semibold text-primary underline">
            See every fee
          </Link>{" "}
          or{" "}
          <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
            call {FIRM.phone}
          </a>
          .
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Local conveyancing:{" "}
          <Link to="/conveyancing/$suburb" params={{ suburb: "caloundra" }} className="font-semibold text-primary underline">
            Caloundra conveyancing
          </Link>{" "}
          ·{" "}
          <Link to="/conveyancing-sunshine-coast" className="font-semibold text-primary underline">
            Sunshine Coast conveyancing
          </Link>{" "}
          ·{" "}
          <Link to="/wills-estate-lawyers-sunshine-coast" className="font-semibold text-primary underline">
            Sunshine Coast wills and estates
          </Link>
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Business and property advice:{" "}
          <Link to="/commercial-conveyancing" className="font-semibold text-primary underline">
            Commercial conveyancing
          </Link>{" "}
          ·{" "}
          <Link to="/retail-shop-leases" className="font-semibold text-primary underline">
            Retail lease review and Form 12 certificates
          </Link>
        </p>
      </section>

      {/* Why choose us */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div>
            <h2 className="font-serif text-3xl text-foreground sm:text-4xl">Why choose us</h2>
            <p className="measure mt-4 text-base leading-relaxed text-muted-foreground">
              You deal directly with the lawyer doing the work — from the first phone call through
              to settlement or signing.
            </p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {PEOPLE.map((p) => (
                <div key={p.slug} className="flex flex-col items-center gap-4 rounded-sm border border-border bg-card p-5 text-center">
                  <img
                    src={p.photo}
                    alt={`${p.name}, ${p.role} at ${FIRM.name}`}
                    loading="lazy"
                    width={256}
                    height={256}
                    className="size-56 shrink-0 shadow-md rounded-full border border-border object-cover object-top sm:size-64"
                  />
                  <div>
                    <p className="font-serif text-lg text-foreground">{p.name}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {p.role}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {PERSON_ONE_LINER[p.slug]}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: BadgeDollarSign,
                  t: "The fee is fixed before we start",
                  d: "You know what the legal work costs at the outset. Disbursements are passed on at cost with nothing added.",
                },
                {
                  icon: FileSearch,
                  t: "Free contract review before you sign",
                  d: "The cheapest time to fix a contract problem is before you are bound by it. Send it to us first — the review costs you nothing.",
                },
                {
                  icon: Scale,
                  t: "Two decades of court experience",
                  d: "Michael has been admitted since 2003 and has litigated property and estate disputes — which shapes how we draft.",
                },
                {
                  icon: Award,
                  t: "Over 40 years' combined experience",
                  d: "Between Michael and Erin, thousands of Queensland property settlements completed — so the issues that derail a matter get spotted early.",
                },
                {
                  icon: MessageCircle,
                  t: "Plain English, always",
                  d: "You should finish every conversation knowing exactly what happens next and what it costs.",
                },
              ].map((item) => (
                <div key={item.t} className="rounded-sm border border-border bg-card p-6">
                  <span className="inline-flex size-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <item.icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-serif text-lg text-foreground">{item.t}</h3>
                  <p className="mt-2 text-base leading-relaxed text-muted-foreground">{item.d}</p>
                </div>
              ))}
            </div>
            <Link
              to="/our-people"
              className="mt-8 inline-block text-sm font-semibold text-primary underline"
            >
              Meet Michael and Erin
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="home-reviews py-20">
        <div className="mx-auto max-w-6xl px-5">
          <ReviewStrip heading="What clients say" />
        </div>
      </section>

      {/* Guides */}
      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-6xl px-5 py-20">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="font-serif text-3xl text-foreground sm:text-4xl">
              Queensland law, explained
            </h2>
            <Link to="/guides" className="text-sm font-semibold text-primary underline">
              Search all guides
            </Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {GUIDES.slice(0, 3).map((g) => (
              <Link
                key={g.slug}
                to="/guides/$slug"
                params={{ slug: g.slug }}
                className="group rounded-sm border border-border bg-card p-7 card-elevate"
              >
                <p className="text-xs font-semibold uppercase tracking-widest text-accent-foreground/70">
                  {g.category}
                </p>
                <h3 className="mt-3 font-serif text-xl leading-snug text-foreground">{g.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{g.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-20">
        <h2 className="font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
        <p className="mt-3 text-sm text-muted-foreground">Tap a question to see the answer.</p>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>
      </section>


      {/* Local Pack block: name, address, phone, hours and reviews, in crawlable HTML
          and matching the Google Business Profile listing word for word. */}
      <section className="border-t border-border">
        <div className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="font-serif text-3xl text-foreground sm:text-4xl">
            Visit us on the Sunshine Coast or the Redcliffe Peninsula
          </h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Golden Beach office
              </h3>
              <address className="mt-3 not-italic text-base leading-relaxed text-foreground">
                <span className="font-semibold">{FIRM.name}</span>
                <br />
                {FIRM.street}
                <br />
                {FIRM.suburb} {FIRM.state} {FIRM.postcode}
                <br />
                <a className="underline underline-offset-4" href={FIRM.phoneHref}>
                  {FIRM.phone}
                </a>
                <br />
                <a className="underline underline-offset-4" href={FIRM.emailHref}>
                  {FIRM.email}
                </a>
              </address>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Peninsula office
              </h3>
              <address className="mt-3 not-italic text-base leading-relaxed text-foreground">
                <span className="font-semibold">{FIRM.name} — Scarborough</span>
                <br />
                {PENINSULA_OFFICE.street}
                <br />
                {PENINSULA_OFFICE.suburb} {PENINSULA_OFFICE.state} {PENINSULA_OFFICE.postcode}
                <br />
                <a className="underline underline-offset-4" href={MICHAEL_PHONE_HREF}>
                  {MICHAEL_PHONE}
                </a>
                <br />
                {PENINSULA_OFFICE.note}
              </address>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                Opening hours
              </h3>
              <p className="mt-3 text-base leading-relaxed text-foreground">
                Monday to Friday, 8:30am – 5:00pm
                <br />
                After-hours and home appointments by arrangement
              </p>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                {FIRM.membership}.
              </p>
              <a
                href={GOOGLE_PROFILE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex text-sm font-semibold text-foreground underline underline-offset-4"
              >
                Read and leave a Google review
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Inline enquiry */}
      <section id="enquire" className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-3xl px-5 py-20">
          <EnquiryForm heading="Tell us about your matter" />
        </div>
      </section>

      <CtaBand />

      <div className="h-20" />
    </>
  );
}
